// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { useResizeObserver, useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import Dropdown from '../dropdown/internal';
import { useInternalI18n } from '../i18n/context';
import { getBaseProps } from '../internal/base-component';
import { getBreakpointValue } from '../internal/breakpoints';
import { warnOnUnsupportedGroupedControlProps } from '../internal/components/control-group-styles/dev-warnings';
import DropdownFooter from '../internal/components/dropdown-footer/index.js';
import ScreenreaderOnly from '../internal/components/screenreader-only';
import { ResetGroupedControlContext, useGroupedControlContext } from '../internal/context/control-group-context';
import { useFormFieldContext } from '../internal/context/form-field-context';
import { getDropdownMinWidth } from '../internal/utils/get-dropdown-min-width';
import { joinStrings } from '../internal/utils/strings';
import Filter from '../select/parts/filter';
import PlainList from '../select/parts/plain-list';
import Trigger from '../select/parts/trigger';
import VirtualList from '../select/parts/virtual-list';
import { composeDropdownContent } from '../select/utils/dropdown-customization';
import InternalTokenGroup from '../token-group/internal';
import { useMultiselect } from './use-multiselect';
import styles from './styles.css.js';
const InternalMultiselect = React.forwardRef(({ options, filteringType, filteringPlaceholder, filteringAriaLabel, filteringClearAriaLabel, ariaRequired, placeholder, disabled, readOnly, ariaLabel, inlineLabelText, selectedOptions, deselectAriaLabel, tokenLimit, i18nStrings, virtualScroll, inlineTokens, hideTokens, expandToViewport, tokenLimitShowFewerAriaLabel, tokenLimitShowMoreAriaLabel, __internalRootRef, autoFocus, enableSelectAll, renderOption, renderDropdownHeader, renderDropdownFooter, dropdownRole, dropdownAriaDescribedby, ...restProps }, externalRef) => {
    var _a;
    const baseProps = getBaseProps(restProps);
    const formFieldContext = useFormFieldContext(restProps);
    const groupedControlProps = useGroupedControlContext();
    const i18n = useInternalI18n('multiselect');
    const isGrouped = !!groupedControlProps.position;
    warnOnUnsupportedGroupedControlProps({
        isGrouped,
        componentName: 'Multiselect',
        invalid: formFieldContext.invalid,
        warning: formFieldContext.warning,
        readOnly,
        inlineTokens,
        expandToViewport,
    });
    // When rendered inside a control group, tokens are always shown inline in the
    // trigger regardless of the `inlineTokens` prop, since there is no room to
    // display tokens below the control within a group.
    const showTokensInline = inlineTokens || isGrouped;
    // When grouped, the dropdown is portaled regardless of the public
    // `expandToViewport` prop so it escapes the control slot's stacking context
    // and paints over the group's inline label (which the focus ring stays under).
    const effectiveExpandToViewport = expandToViewport || isGrouped;
    const selfControlId = useUniqueId('trigger');
    const controlId = (_a = formFieldContext.controlId) !== null && _a !== void 0 ? _a : selfControlId;
    const ariaLabelId = useUniqueId('multiselect-ariaLabel-');
    const footerId = useUniqueId('multiselect-footer-');
    const [filteringValue, setFilteringValue] = useState('');
    const multiselectProps = useMultiselect({
        options,
        selectedOptions,
        filteringType,
        disabled,
        deselectAriaLabel,
        controlId,
        ariaLabelId,
        footerId,
        filteringValue,
        setFilteringValue,
        externalRef,
        enableSelectAll,
        i18nStrings,
        dropdownRole,
        dropdownAriaDescribedby,
        ...restProps,
    });
    const dropdownContentProps = {
        filterText: filteringValue,
        closeDropdown: multiselectProps.closeDropdown,
    };
    const customDropdownHeader = renderDropdownHeader === null || renderDropdownHeader === void 0 ? void 0 : renderDropdownHeader(dropdownContentProps);
    const customDropdownFooter = renderDropdownFooter === null || renderDropdownFooter === void 0 ? void 0 : renderDropdownFooter(dropdownContentProps);
    const filter = (React.createElement(Filter, { clearAriaLabel: filteringClearAriaLabel, filteringType: filteringType, placeholder: filteringPlaceholder, ariaLabel: filteringAriaLabel, ariaRequired: ariaRequired, value: filteringValue, ...multiselectProps.getFilterProps() }));
    const triggerRef = useRef(null);
    const [triggerWidth, setTriggerWidth] = useState(null);
    useResizeObserver(() => triggerRef.current, entry => entry.borderBoxWidth > 0 && setTriggerWidth(entry.borderBoxWidth));
    const trigger = (React.createElement(Trigger, { ref: triggerRef, placeholder: placeholder, disabled: disabled, readOnly: readOnly, triggerProps: multiselectProps.getTriggerProps(disabled, autoFocus), selectedOption: null, selectedOptions: selectedOptions, triggerVariant: showTokensInline ? 'tokens' : 'placeholder', isOpen: multiselectProps.isOpen, groupedControlProps: groupedControlProps, inlineLabelText: inlineLabelText, ...formFieldContext, controlId: controlId, ariaLabelledby: joinStrings(formFieldContext.ariaLabelledby, ariaLabelId) }));
    const tokens = selectedOptions.map(option => ({
        label: option.label,
        disabled,
        labelTag: option.labelTag,
        description: option.description,
        iconAlt: option.iconAlt,
        iconName: option.iconName,
        iconUrl: option.iconUrl,
        iconSvg: option.iconSvg,
        tags: option.tags,
        dismissLabel: i18n('deselectAriaLabel', deselectAriaLabel === null || deselectAriaLabel === void 0 ? void 0 : deselectAriaLabel(option), format => { var _a; return format({ option__label: (_a = option.label) !== null && _a !== void 0 ? _a : '' }); }),
        _readOnly: !!option.disabled,
    }));
    const ListComponent = virtualScroll ? VirtualList : PlainList;
    const showTokens = !hideTokens && !showTokensInline && tokens.length > 0;
    const tokenGroupI18nStrings = {
        limitShowFewer: i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.tokenLimitShowFewer,
        limitShowMore: i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.tokenLimitShowMore,
    };
    const dropdownStatus = multiselectProps.dropdownStatus;
    const dropdownProps = multiselectProps.getDropdownProps();
    const hasFilteredOptions = multiselectProps.filteredOptions.length > 0;
    const statusFooter = dropdownStatus.isSticky ? (React.createElement(DropdownFooter, { content: multiselectProps.isOpen ? dropdownStatus.content : null, id: footerId })) : null;
    const { header: dropdownHeader, footer: dropdownFooter } = composeDropdownContent({
        filter,
        customDropdownHeader,
        customDropdownFooter,
        statusFooter,
        dropdownHeaderClass: styles['dropdown-header'],
        dropdownFooterClass: styles['dropdown-footer'],
    });
    const hasOptions = useRef(options.length > 0);
    hasOptions.current = hasOptions.current || options.length > 0;
    return (React.createElement(ResetGroupedControlContext, null,
        React.createElement("div", { ...baseProps, ref: __internalRootRef, className: clsx(styles.root, baseProps.className), ...multiselectProps.getWrapperProps() },
            React.createElement(Dropdown, { ...dropdownProps, ariaLabelledby: dropdownProps.ariaRole ? joinStrings(ariaLabelId, controlId) : undefined, ariaDescribedby: dropdownProps.ariaRole
                    ? joinStrings(dropdownStatus.content ? footerId : undefined, dropdownAriaDescribedby)
                    : undefined, open: multiselectProps.isOpen, minWidth: getDropdownMinWidth({ expandToViewport: effectiveExpandToViewport, triggerWidth }), maxWidth: getBreakpointValue('xxs'), trigger: trigger, header: dropdownHeader, footer: dropdownFooter, expandToViewport: effectiveExpandToViewport, 
                // Forces dropdown position recalculation when new options are loaded
                contentKey: hasOptions.current.toString(), content: React.createElement(ListComponent, { renderOption: renderOption, listBottom: !dropdownStatus.isSticky ? (React.createElement(DropdownFooter, { content: multiselectProps.isOpen ? dropdownStatus.content : null, id: footerId })) : null, menuProps: { ...multiselectProps.getMenuProps(), ariaRequired }, getOptionProps: multiselectProps.getOptionProps, filteredOptions: multiselectProps.filteredOptions, filteringValue: filteringValue, ref: multiselectProps.scrollToIndex, hasDropdownStatus: dropdownStatus.content !== null, checkboxes: true, useInteractiveGroups: true, screenReaderContent: multiselectProps.announcement, highlightType: multiselectProps.highlightType, firstOptionSticky: hasFilteredOptions && enableSelectAll, isMultiSelect: true }) }),
            showTokens && (React.createElement(InternalTokenGroup, { ...multiselectProps.getTokenProps(), className: styles.tokens, alignment: "horizontal", limit: tokenLimit, items: tokens, i18nStrings: tokenGroupI18nStrings, limitShowMoreAriaLabel: tokenLimitShowMoreAriaLabel, limitShowFewerAriaLabel: tokenLimitShowFewerAriaLabel, disableOuterPadding: true, readOnly: readOnly, isItemReadOnly: item => item._readOnly })),
            React.createElement(ScreenreaderOnly, { id: ariaLabelId }, ariaLabel || inlineLabelText))));
});
export default InternalMultiselect;
//# sourceMappingURL=internal.js.map